import {useMemo, useState} from "react";
import {Highlight, ScrollArea, Text, TextInput} from "@mantine/core";
import {useDebouncedValue} from "@mantine/hooks";
import {notifications} from "@mantine/notifications";
import {matchSorter} from "match-sorter";
import {Trans, useTranslation} from "react-i18next";
import MinusSvg from "../../../../assets/minus.svg?react";
import PlusSvg from "../../../../assets/plus.svg?react";
import {dibAppApi} from "../../../../dibAppApi/dibAppApi";
import {ClassSelectors} from "../../../../types";
import {bffApi} from "../../../chat/bffApi";
import type {CollectionCollaborator} from "../../types";
import {ChecklistModal} from "../ChecklistModal";
import classes from "../modalStyles.module.css";

type Person = {id?: string; name: string; email: string};

type ShareFillingModalProps = {
    opened: boolean;
    onClose: () => void;
    collectionId: string;
    topicId: string;
    collaborators: CollectionCollaborator[];
};

type PersonRowProps = {
    person: Person;
    type: "add" | "remove";
    hint: string;
    query: string;
    onToggle: (person: Person) => void;
};

const PersonRow = ({person, type, hint, query, onToggle}: PersonRowProps) => (
    <button
        type="button"
        className={`${classes.shareRow} ${
            type === "add" ? classes.shareRowAdd : classes.shareRowRemove
        }`}
        title={hint}
        onClick={() => onToggle(person)}
    >
        <span className={classes.shareRowInfo}>
            <Highlight
                component="span"
                className={classes.shareRowName}
                highlight={query}
            >
                {person.name}
            </Highlight>
            <Highlight
                component="span"
                size="xs"
                className={classes.shareRowEmail}
                highlight={query}
            >
                {person.email}
            </Highlight>
        </span>
        <span className={classes.shareRowHint} aria-hidden>
            {hint}
            {type === "add" ? <PlusSvg /> : <MinusSvg />}
        </span>
    </button>
);

export const ShareFillingModal = ({
    opened,
    onClose,
    collectionId,
    topicId,
    collaborators,
}: ShareFillingModalProps) => {
    const {t} = useTranslation();
    const {data: user} = bffApi.endpoints.getUser.useQuery();
    const isDemo = !!user?.demokonto;
    const selfEmail = user?.epost?.toLowerCase();

    const [query, setQuery] = useState("");
    const [debouncedQuery] = useDebouncedValue(query, 300);

    const employeesQuery = dibAppApi.endpoints.getEmployees.useQuery(
        debouncedQuery,
        {skip: isDemo},
    );
    const [updateCollaborators, {isLoading: isCommitting}] =
        dibAppApi.endpoints.updateCollectionCollaborators.useMutation();

    // Current collaborators minus the current user; email is the identity
    // key throughout (as upstream), so email-less entries are dropped.
    const initial = useMemo<Person[]>(
        () =>
            collaborators.flatMap((c) =>
                c.email && c.email.toLowerCase() !== selfEmail
                    ? [{id: c.id, name: c.name, email: c.email}]
                    : [],
            ),
        [collaborators, selfEmail],
    );

    // Staged "Your collaborators" list — local until Confirm. The modal is
    // conditionally mounted, so every open starts fresh from `initial`.
    const [staged, setStaged] = useState<Person[]>(initial);

    // Server-filtered employees, minus self and staged, ranked client-side
    // with the same matchSorter pass as upstream.
    const available = useMemo<Person[]>(() => {
        const stagedEmails = new Set(staged.map((p) => p.email.toLowerCase()));
        const candidates = (employeesQuery.data ?? []).flatMap((e) =>
            e.epost &&
            e.navn &&
            e.epost.toLowerCase() !== selfEmail &&
            !stagedEmails.has(e.epost.toLowerCase())
                ? [{name: e.navn, email: e.epost}]
                : [],
        );
        return matchSorter(candidates, debouncedQuery, {
            keys: ["name", "email"],
        });
    }, [employeesQuery.data, staged, selfEmail, debouncedQuery]);

    const initialEmails = new Set(initial.map((p) => p.email.toLowerCase()));
    const stagedEmails = new Set(staged.map((p) => p.email.toLowerCase()));
    const toAdd = staged.filter(
        (p) => !initialEmails.has(p.email.toLowerCase()),
    );
    const toRemove = initial.filter(
        (p) => !stagedEmails.has(p.email.toLowerCase()),
    );
    const hasChanged = toAdd.length > 0 || toRemove.length > 0;

    const toggle = (person: Person) =>
        setStaged((prev) =>
            prev.some((p) => p.email === person.email)
                ? prev.filter((p) => p.email !== person.email)
                : [...prev, person],
        );

    const handleCommit = async () => {
        try {
            const result = await updateCollaborators({
                collectionId,
                topicId,
                toAdd: toAdd.map(({name, email}) => ({name, email})),
                toRemove: toRemove.flatMap((p) => (p.id ? [{id: p.id}] : [])),
            }).unwrap();

            const addedOk = result.added.filter(
                (r) => r.status === "fulfilled",
            ).length;
            const removedOk = result.removed.filter(
                (r) => r.status === "fulfilled",
            ).length;
            // Results arrive in request order; removal results carry no
            // person data, so failure names come from our input by index.
            const failedAddNames = result.added.flatMap((r, i) =>
                r.status === "rejected"
                    ? [r.data?.name ?? toAdd[i]?.name ?? ""]
                    : [],
            );
            const failedRemoveNames = result.removed.flatMap((r, i) =>
                r.status === "rejected" ? [toRemove[i]?.name ?? ""] : [],
            );

            const successParts: string[] = [];
            if (addedOk > 0) {
                successParts.push(
                    t("Info.SuccessfullyAddedCollaborators", {
                        count: addedOk,
                        defaultValue_one: "Added one collaborator.",
                        defaultValue_other: "Added {{count}} collaborators.",
                    }),
                );
            }
            if (removedOk > 0) {
                successParts.push(
                    t("Info.SuccessfullyRemovedCollaborators", {
                        count: removedOk,
                        defaultValue_one: "Removed one collaborator.",
                        defaultValue_other: "Removed {{count}} collaborators.",
                    }),
                );
            }
            if (successParts.length > 0) {
                notifications.show({message: successParts.join(" ")});
            }

            const failureParts: string[] = [];
            if (failedAddNames.length > 0) {
                failureParts.push(
                    t("Error.FailedToAddCollaborators", {
                        names: failedAddNames.join(", "),
                        defaultValue: "Failed to add {{names}}",
                    }),
                );
            }
            if (failedRemoveNames.length > 0) {
                failureParts.push(
                    t("Error.FailedToRemoveCollaborators", {
                        names: failedRemoveNames.join(", "),
                        defaultValue: "Failed to remove {{names}}",
                    }),
                );
            }
            if (failureParts.length > 0) {
                notifications.show({
                    message: failureParts.join(" "),
                    color: "red",
                });
            }

            // Close even on partial failure (upstream behavior); the
            // CollectionData invalidation refetches the collaborator list.
            onClose();
        } catch {
            notifications.show({
                message: t("Error.SomethingWentWrong"),
                color: "red",
            });
        }
    };

    const removeHint = t("Phrase.RemoveFromSharing", {
        defaultValue: "Remove from sharing",
    });
    const addHint = t("Word.Add", {defaultValue: "Add"});

    return (
        <ChecklistModal
            opened={opened}
            onClose={onClose}
            title={t("Phrase.ShareFilling", {defaultValue: "Share filling"})}
            primary={
                isDemo
                    ? undefined
                    : {
                          label: t("Phrase.ConfirmChoice", {
                              defaultValue: "Confirm selection",
                          }),
                          onClick: handleCommit,
                          loading: isCommitting,
                          disabled: !hasChanged || isCommitting,
                          title: hasChanged
                              ? t("Phrase.ShareFilling", {
                                    defaultValue: "Share filling",
                                })
                              : t("Phrase.ListOfCollaboratorsNotChanged", {
                                    defaultValue:
                                        "The list of collaborators has not been changed",
                                }),
                      }
            }
            secondary={
                isDemo ? undefined : {label: t("Word.Abort"), onClick: onClose}
            }
        >
            <div className={classes.modalBody}>
                {isDemo ? (
                    <Text size="sm" className={classes.modalText}>
                        {t("Phrase.SharingNotAvailableForDemoUsers", {
                            defaultValue:
                                "Sorry, sharing is not available for demo users",
                        })}
                    </Text>
                ) : (
                    <>
                        <Text size="sm" className={classes.modalText}>
                            {t("Phrase.CollaborateOnFillingRealtime", {
                                defaultValue:
                                    "Collaborate in real-time. You will remain the owner of the filling, and may at any time remove collaborators",
                            })}
                        </Text>

                        <TextInput
                            label={t("Profile.SearchInList", {
                                defaultValue: "Search in list",
                            })}
                            value={query}
                            onChange={(e) => setQuery(e.currentTarget.value)}
                            classNames={{
                                root: classes.selectRoot,
                                label: classes.selectLabel,
                            }}
                        />

                        <div className={classes.modalSection}>
                            <div className={classes.subheader}>
                                {t("Phrase.YourCollaborators", {
                                    defaultValue: "Your collaborators",
                                })}
                            </div>
                            <Text size="sm" className={classes.modalText}>
                                {t("Phrase.AddPeopleFromList", {
                                    defaultValue:
                                        "Add collaborators from the list below",
                                })}
                            </Text>
                            {staged.length > 0 && (
                                <ScrollArea.Autosize mah={166}>
                                    {staged.map((person) => (
                                        <PersonRow
                                            key={person.email}
                                            person={person}
                                            type="remove"
                                            hint={removeHint}
                                            query={debouncedQuery}
                                            onToggle={toggle}
                                        />
                                    ))}
                                </ScrollArea.Autosize>
                            )}
                        </div>

                        <div className={classes.modalSection}>
                            <div className={classes.subheader}>
                                {t("Phrase.AvailableForSharing", {
                                    defaultValue: "Available for sharing",
                                })}
                            </div>
                            {employeesQuery.isError && (
                                <>
                                    <Text
                                        size="sm"
                                        className={classes.modalText}
                                    >
                                        {t("Error.ErrorUploadingContactList", {
                                            defaultValue:
                                                "Error loading list of contacts",
                                        })}
                                    </Text>
                                    <Text
                                        size="sm"
                                        className={classes.modalText}
                                    >
                                        <Trans
                                            i18nKey="Error.TryReloadingOrContactUs"
                                            components={{
                                                "span-1": <span />,
                                                "a-1": (
                                                    <a
                                                        href="#"
                                                        className={
                                                            ClassSelectors.intercomLauncher
                                                        }
                                                    />
                                                ),
                                            }}
                                        />
                                    </Text>
                                </>
                            )}
                            {employeesQuery.isFetching ? (
                                <Text size="sm" className={classes.modalText}>
                                    {t("Word.SearchingEllipsis", {
                                        defaultValue: "Searching...",
                                    })}
                                </Text>
                            ) : (
                                employeesQuery.isSuccess && (
                                    <ScrollArea.Autosize mah={200}>
                                        {available.map((person) => (
                                            <PersonRow
                                                key={person.email}
                                                person={person}
                                                type="add"
                                                hint={addHint}
                                                query={debouncedQuery}
                                                onToggle={toggle}
                                            />
                                        ))}
                                    </ScrollArea.Autosize>
                                )
                            )}
                        </div>
                    </>
                )}
            </div>
        </ChecklistModal>
    );
};
